// Teaching stories, not an emulator, firmware configuration or hardware API.
// Grounding: Aeroponic/Core/Inc/aeroponic_state_machine.hpp (manual pump-only,
// independent thermal permit, sensor fallback), aeroponic_control_config.h
// (adaptive output locked), and docs/aeroponic_control_architecture.md.
// A click advances a story; it never represents elapsed physical time.

export const TWIN_SCENARIO_DISCLAIMER = "Hypothetisches Lernmodell: keine Live-Daten, keine Gerätebefehle und keine Sicherheitsgarantie für eine reale Anlage. Jeder Schritt wird nur durch deine Auswahl weitergeschaltet.";

function deepFreeze(value) {
  Object.values(value).forEach(child => {
    if (child && typeof child === "object") deepFreeze(child);
  });
  return Object.freeze(value);
}

function step(id, title, focusPart, explanation, visual = {}) {
  return {
    id, title, focusPart,
    localState: "Wartet (Demo)",
    portalState: "Verbunden (Demo)",
    measurementQuality: "Nicht gemessen",
    ...explanation,
    visual: { pumpActive: false, airPath: null, connectionLost: false, ...visual },
  };
}

export const feederTwinScenarios = deepFreeze([
  {
    id: "timed-cycle",
    title: "Ein normaler Zeitzyklus",
    summary: "Verstehe Sprühphase und Pause im lokalen Zeitbetrieb. Die Luftmessung gehört nicht zu diesem manuellen Pumpenzyklus.",
    evidenceLevel: "Didaktische Folge nach dem Timer-Prinzip, keine Anlagenempfehlung.",
    steps: [
      step("permit", "Zuerst muss der lokale Start erlaubt sein", "controller", {
        observation: "Für das Beispiel ist ein Zeitprogramm ausgewählt. Ob die echte Pumpe bereits ausreichend geruht hat, ist hier nicht bekannt.",
        decision: "Der lokale Controller prüft die Startbedingungen und die vorgeschriebene Pumpenruhe. Ein fälliger Zeitplan allein reicht nicht aus.",
        localExecution: "Die Pumpe bleibt in dieser Vorschau aus. Erst der nächste Lernschritt nimmt eine gültige Freigabe an.",
        evidenceBoundary: "Die Seite prüft weder ein Gerät noch dessen Fehlerzustand, Kühlung oder Inbetriebnahme.",
        localState: "Freigabe prüfen (Demo)",
      }),
      step("spray", "Eine begrenzte Sprühphase", "nozzle", {
        observation: "In diesem erfundenen Beispiel sind Zeitprogramm und Pumpenfreigabe erfüllt.",
        decision: "Der STM32 würde die Pumpe für die lokal eingestellte, begrenzte Sprühphase einschalten.",
        localExecution: "Das Modell markiert Pumpe und Düsen als aktiv. Im manuellen Zeitbetrieb bleibt der Luftmesspfad aus.",
        evidenceBoundary: "Keine echte Pumpe läuft. Es werden weder Druck noch Tropfengröße, Wassermenge oder eine geeignete Sprühdauer gemessen.",
        localState: "Sprühphase (Demo)",
      }, { pumpActive: true }),
      step("pause", "Nach der Sprühphase folgt eine Pause", "pump", {
        observation: "Wir nehmen an, dass die eingestellte Sprühphase beendet ist; der Klick selbst ist keine Zeitmessung.",
        decision: "Die lokale Steuerung beendet den Pumpenimpuls. Die nächste Freigabe hängt auch von der erforderlichen Ruhezeit ab.",
        localExecution: "Im Modell ist die Pumpe aus. Es werden keine Luftmessung und kein schnellerer Neustart durch diese Seite ausgelöst.",
        evidenceBoundary: "Das ist kein Nachweis, dass ein realer Treiber rechtzeitig abschaltet. Dazu braucht es die Hardwareprüfung.",
        localState: "Pause und Pumpenruhe (Demo)",
      }),
      step("next-cycle", "Der nächste Zyklus ist wieder eine lokale Entscheidung", "controller", {
        observation: "Am Ende des Beispiels könnte der nächste Termin erreicht sein. Die reale Restzeit kennen wir nicht.",
        decision: "Auch beim nächsten Termin müssen Pumpenschutz und Freigabe erfüllt sein; ein Termin überstimmt keinen Motorfehler.",
        localExecution: "Diese Lernfolge endet mit ausgeschalteter Modellpumpe. Sie wiederholt sich nicht automatisch.",
        evidenceBoundary: "Keine angezeigte Reihenfolge ersetzt eine geeignete, geprüfte Einstellung für Pflanze und Anlage.",
        localState: "Nächste Freigabe offen (Demo)",
      }),
    ],
  },
  {
    id: "invalid-measurement",
    title: "Ein Messwert ist ungültig",
    summary: "Eine fehlerhafte Beobachtung darf nicht als Pflanzenzustand gelten. Der lokale Zeit-Rückfall bleibt vom Pumpenschutz abhängig.",
    evidenceLevel: "Vereinfachtes Mess-/Shadow-Beispiel, kein universeller Fehlerablauf.",
    steps: [
      step("observe", "Luft aus dem Wurzelraum beobachten", "sensors", {
        observation: "Für diese Szene nehmen wir einen Messzyklus nach abgeschlossener Sprühphase und bereits geprüfter Außenluftreferenz an.",
        decision: "Im Messbetrieb wird die Qualität der RH/T-Beobachtung geprüft, bevor daraus eine Auswertung entsteht.",
        localExecution: "Die Modellpumpe ist aus. Der gewählte Luftweg zeigt nur die Wurzelluftmessung derselben Messkammer.",
        evidenceBoundary: "Die Messkammerbeobachtung ist kein direkt gemessener Wasserzustand der Pflanze.",
        localState: "Wurzelluft beobachten (Demo)",
        measurementQuality: "Qualität noch offen",
      }, { airPath: "root" }),
      step("reject", "Den fehlerhaften Wert nicht verwenden", "sensors", {
        observation: "Wir setzen für das Beispiel einen ungültigen RH/T-Wert voraus, etwa durch fehlende oder unbrauchbare Daten.",
        decision: "Dieser Wert wird nicht als gültiger Pflanzenzustand interpretiert und nicht durch einen erfundenen Messwert ersetzt.",
        localExecution: "Die Modell-Messphase endet. Die Pumpe bleibt in diesem Schritt aus; ein Sensorfehler allein bestätigt keine erfolgreiche Bewässerung.",
        evidenceBoundary: "Ein bestimmter Sensorfehler und seine Ursache sind hier nur angenommen, nicht am realen Sensor diagnostiziert.",
        localState: "Messauswertung verworfen (Demo)",
        measurementQuality: "Ungültig (angenommen)",
      }),
      step("fallback", "Auf den lokalen Zeitplan zurückfallen", "controller", {
        observation: "Für eine messwertabhängige Auswertung fehlt nun eine belastbare Grundlage.",
        decision: "Das Prinzip des Rückfalls ist der bestehende Zeitplan. Er darf aber weder die nötige Pumpenruhe noch einen Motorfehler übergehen.",
        localExecution: "Im Modell wird auf eine gültige lokale Pumpenfreigabe gewartet. Es gibt keinen zusätzlichen Impuls aufgrund des fehlerhaften Wertes.",
        evidenceBoundary: "Sensor-Rückfall ist keine Garantie gegen jeden Ausfall. Insbesondere ein Pumpenfehler ist kein Sensorproblem.",
        localState: "Zeit-Rückfall wartet (Demo)",
        measurementQuality: "Ungültig (angenommen)",
      }),
      step("bounded-restart", "Nur mit eigener Freigabe weiter", "pump", {
        observation: "Nur für diesen nächsten Lernschritt nehmen wir einen fälligen Timer, ausreichende Pumpenruhe und keinen Pumpenfehler an.",
        decision: "Die angenommene Freigabe stammt aus dem lokalen Zeit-/Schutzpfad, nicht aus dem ungültigen Sensorwert.",
        localExecution: "Ein begrenzter Modellimpuls illustriert den Zeit-Rückfall. Es wird kein Gerätebefehl versendet.",
        evidenceBoundary: "Die Szene bescheinigt keine reale Freigabe und ist kein gültiger Ersatz für Messung oder Inbetriebnahme.",
        localState: "Zeitimpuls unter Annahmen (Demo)",
        measurementQuality: "Weiterhin ungültig",
      }, { pumpActive: true }),
      step("follow-up", "Den Fehler anschließend nachvollziehen", "gateway", {
        observation: "Das Beispiel endet nach dem angenommenen Impuls. Der fehlerhafte Messwert bleibt als solcher erkennbar.",
        decision: "Fehlergrund und Zeitpunkt müssen bei realen Daten nachvollziehbar sein. Ein späterer gültiger Wert macht den alten Wert nicht nachträglich gültig.",
        localExecution: "Die Modellpumpe ist wieder aus. Kein Sensor wird durch diese Seite zurückgesetzt oder neu kalibriert.",
        evidenceBoundary: "Erst echte Diagnose- und Prüfdaten zeigen, ob Sensor, Verbindung oder Anlage wieder verwendbar sind.",
        localState: "Diagnose erforderlich (Demo)",
        measurementQuality: "Fehler bleibt sichtbar",
      }),
    ],
  },
  {
    id: "portal-offline",
    title: "Die Verbindung zum Portal bricht ab",
    summary: "Portal und ESP32-Gateway übertragen Beobachtungen. Die Aeroponic-Pumpensteuerung bleibt eine getrennte lokale Aufgabe des STM32.",
    evidenceLevel: "Angenommener reiner Übertragungsausfall; Strom- und Gerätefehler sind nicht eingeschlossen.",
    steps: [
      step("connected", "Zwei getrennte Aufgaben", "gateway", {
        observation: "Für das Beispiel sind Gateway und Portal erreichbar; Messwerte werden auf dieser Seite trotzdem nicht abgerufen.",
        decision: "Der Gateway überträgt Beobachtungen. Pumpenzeitplan und Pumpenschutz werden nicht vom Portal berechnet oder freigegeben.",
        localExecution: "Die Szene zeigt einen lokalen Controller und eine getrennte Verbindung zur Anzeige.",
        evidenceBoundary: "Das Verbindungssymbol ist erfunden. Es sagt nichts über die Erreichbarkeit deines Geräts aus.",
        localState: "Lokaler Zeitplan bereit (Demo)",
      }),
      step("lost", "Nur der Übertragungsweg fällt aus", "gateway", {
        observation: "Jetzt nehmen wir ausschließlich einen Verbindungsabbruch zwischen Gateway und Portal an.",
        decision: "Eine fehlende Portalverbindung ist keine neue Pumpenanweisung. Die Anzeige darf alte Beobachtungen nicht als frisch ausgeben.",
        localExecution: "Die Modellverbindung wird unterbrochen. Der lokale Zeit-/Schutzpfad bleibt davon getrennt.",
        evidenceBoundary: "Ein Stromausfall oder defekter STM32 wäre ein anderer Fehlerfall. Lokale Autonomie ist keine Ausfallgarantie.",
        localState: "Lokaler Zeitplan unverändert (Demo)",
        portalState: "Nicht erreichbar (Demo)",
        measurementQuality: "Keine neuen Portaldaten",
      }, { connectionLost: true }),
      step("local-cycle", "Ein lokaler Zyklus braucht das Portal nicht", "controller", {
        observation: "Im Beispiel läuft die Hardware weiterhin; wir nehmen erneut erfüllte lokale Timer- und Pumpenfreigaben an.",
        decision: "Der STM32 kann seinen begrenzten Zeitimpuls unabhängig vom Portal ausführen. Pumpenschutz bleibt erforderlich.",
        localExecution: "Nur die Modellpumpe wird als aktiv dargestellt. Die Portalverbindung bleibt unterbrochen.",
        evidenceBoundary: "Es wurde kein Offline-Test an einer realen Anlage durchgeführt und kein erfolgreicher Pumpenlauf gemessen.",
        localState: "Lokaler Zeitimpuls (Demo)",
        portalState: "Nicht erreichbar (Demo)",
        measurementQuality: "Keine neuen Portaldaten",
      }, { pumpActive: true, connectionLost: true }),
      step("local-pause", "Die lokale Pause bleibt bestehen", "pump", {
        observation: "Die angenommene Sprühphase ist abgeschlossen. Im Portal fehlen weiterhin neue Beobachtungen.",
        decision: "Der lokale Controller beendet den Impuls und beachtet die nächste Pause. Fehlende Cloud-Daten verlängern ihn nicht.",
        localExecution: "Die Modellpumpe ist aus, die Übertragungsverbindung weiterhin unterbrochen.",
        evidenceBoundary: "Eine Datenlücke beweist weder einen Pumpenausfall noch einen erfolgreichen Betrieb.",
        localState: "Lokale Pause (Demo)",
        portalState: "Nicht erreichbar (Demo)",
        measurementQuality: "Keine neuen Portaldaten",
      }, { connectionLost: true }),
      step("reconnected", "Nach Wiederverbindung die Daten prüfen", "gateway", {
        observation: "Zum Schluss nehmen wir eine wieder erreichbare Portalverbindung an.",
        decision: "Erst tatsächlich empfangene Datensätze mit Herkunft, Zeit und Qualität dürfen in den Verlauf eingehen. Lücken werden nicht erfunden ergänzt.",
        localExecution: "Das Modell verbindet die Anzeige wieder, ohne einen Pumpenstart auszulösen oder einen Verlauf zu erzeugen.",
        evidenceBoundary: "Ob der separate Aeroponic-Gateway Daten gespeichert und nachgeliefert hat, muss der reale Empfang belegen. Diese Szene tut das nicht.",
        localState: "Lokale Pause unverändert (Demo)",
        portalState: "Wieder erreichbar (Demo)",
        measurementQuality: "Empfang noch zu prüfen",
      }),
    ],
  },
  {
    id: "adaptive-concept",
    title: "Adaptive Bewässerung als Forschungsfrage",
    summary: "Messkurven könnten künftig zusätzliche Hinweise liefern. Eine berechnete Idee ist noch keine freigegebene Bewässerungsregelung.",
    evidenceLevel: "Experimentelle Hypothese. Adaptive Aktorausgabe ist im geprüften Quellstand gesperrt.",
    steps: [
      step("reference", "Vergleichbare Beobachtungen sammeln", "air", {
        observation: "Im Beispiel beginnt eine Messphase an der Außenluftreferenz derselben Messkammer; es werden keine echten Messwerte gezeigt.",
        decision: "Luftweg, Messort und Qualität müssen bekannt sein, bevor Kurven miteinander verglichen werden.",
        localExecution: "Die Szene hebt den Referenzluftweg hervor. Diese Luftführung dient der Messkammer, nicht dem Trocknen der Wurzeln.",
        evidenceBoundary: "Die Animation ist keine Bestätigung von Luftstrom, Sensorqualität oder mechanischer Dichtigkeit.",
        localState: "Referenz beobachten (Demo)",
        measurementQuality: "Nur angenommene Beobachtung",
      }, { airPath: "reference" }),
      step("root-response", "Die Wurzelluft gesondert beobachten", "sensors", {
        observation: "Nun wird im Lernmodell auf den zweiten Luftweg derselben Messkammer umgeschaltet.",
        decision: "Eine Wurzelluftkurve kann zusammen mit Klima und Messqualität untersucht werden; sie ist kein direkter Turgordruck.",
        localExecution: "Der Wurzelluftweg wird hervorgehoben. Es bleibt bei einer schematischen Beobachtung ohne Zahlen oder Pumpenbefehl.",
        evidenceBoundary: "Weder RH/T noch das optionale Leaf-Signal beweisen allein Wassermangel oder den passenden Bewässerungszeitpunkt.",
        localState: "Wurzelluft beobachten (Demo)",
        measurementQuality: "Nur angenommene Beobachtung",
      }, { airPath: "root" }),
      step("shadow", "Eine Hypothese neben dem Zeitplan", "controller", {
        observation: "Angenommene Kurvenmerkmale könnten einen experimentellen Vorschlag für einen späteren Vergleich liefern.",
        decision: "Im Shadow-Prinzip wird eine hypothetische Entscheidung vom tatsächlich maßgeblichen Zeitplan getrennt.",
        localExecution: "Die Modellpumpe bleibt aus. Der Vorschlag schaltet weder ein Gerät noch verändert er Sprüh- oder Pausenzeiten.",
        evidenceBoundary: "Im geprüften Quellstand bleibt adaptive Aktorausgabe gesperrt. Dieses Modell schaltet diese Sperre nicht frei.",
        localState: "Nur hypothetischer Vorschlag",
        measurementQuality: "Kein validierter Pflanzenbefund",
      }),
      step("validate", "Erst prüfen, dann belastbar entscheiden", "roots", {
        observation: "Ein Vorschlag kann plausibel aussehen, ohne bei einer konkreten Pflanze tatsächlich besser zu wirken.",
        decision: "Dafür braucht es Referenzmessungen, reproduzierbare Versuche und getrennte Nachweise für Pflanzenwirkung und technische Sicherheit.",
        localExecution: "Die Lernfolge endet ohne Aktorausgabe. Zeitbetrieb, Messung und wissenschaftliche Freigabe bleiben getrennte Ebenen.",
        evidenceBoundary: "Es werden kein Vorteil gegenüber einem Zeitplan, keine Ertragssteigerung und keine validierte adaptive Regelung versprochen.",
        localState: "Forschungsnachweis offen",
        measurementQuality: "Validierung erforderlich",
      }),
    ],
  },
]);

function findScenario(id) {
  const scenario = feederTwinScenarios.find(item => item.id === id);
  if (!scenario) throw new RangeError("Unbekanntes Lern-Szenario");
  return scenario;
}

function validateState(state) {
  if (!state || typeof state !== "object") throw new TypeError("Ungültiger Lernzustand");
  const scenario = findScenario(state.scenarioId);
  const lastIndex = scenario.steps.length - 1;
  if (!Number.isInteger(state.stepIndex) || state.stepIndex < 0 || state.stepIndex > lastIndex ||
      !["ready", "running", "complete"].includes(state.status) ||
      (state.status === "ready" && state.stepIndex !== 0) ||
      (state.status === "complete" && state.stepIndex !== lastIndex) ||
      (state.status === "running" && state.stepIndex === lastIndex)) {
    throw new RangeError("Ungültiger Lernschritt");
  }
  return scenario;
}

export function createTwinScenarioState(scenarioId = "timed-cycle") {
  findScenario(scenarioId);
  return Object.freeze({ scenarioId, stepIndex: 0, status: "ready" });
}

// Deliberately no tick, clock, network, random input or hardware effects.
// Unknown events do not advance the story; a completed story never loops.
export function transitionTwinScenario(state, event) {
  const scenario = validateState(state);
  if (!event || typeof event !== "object") return state;
  if (event.type === "select") return createTwinScenarioState(event.scenarioId);
  if (event.type === "reset") return createTwinScenarioState(state.scenarioId);
  if (event.type === "start" && state.status === "ready") {
    return Object.freeze({ ...state, status: "running" });
  }
  if (event.type === "next" && state.status === "running") {
    const stepIndex = state.stepIndex + 1;
    const status = stepIndex === scenario.steps.length - 1 ? "complete" : "running";
    return Object.freeze({ scenarioId: state.scenarioId, stepIndex, status });
  }
  return state;
}

export function getTwinScenarioSnapshot(state) {
  const scenario = validateState(state);
  return Object.freeze({
    scenario,
    step: scenario.steps[state.stepIndex],
    stepNumber: state.stepIndex + 1,
    totalSteps: scenario.steps.length,
    isStarted: state.status !== "ready",
    isComplete: state.status === "complete",
  });
}
